import { queryOptions } from '@tanstack/react-query';
import type { usePermissions } from '@customerio/hydra-permissions/react';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { PlanFeature, type AugmentedAccount, type SearchResult } from '@/services/auth/types';
import { defaultFilter } from 'cmdk';
import { createProductTaxonomy, type ProductTaxonomy } from '@/services/product-taxonomy';
import { getNavigationLabels } from './navigation-manifest';

export type PermissionGuard = Pick<ReturnType<typeof usePermissions>, 'hasPermission' | 'hasAnyPermission'> & {
  account: AugmentedAccount | null;
  hasFeatureFlag: (flag: AccountFeatureFlag) => boolean;
};

type GuardFunction = (guard: PermissionGuard) => boolean;

// Routes that redirect builder plan users to the billing upgrade page. Renamed segments list
// both the legacy and renamed paths so the guard matches whichever is canonical for the account
// (Analysis → Reports, Campaigns → Automations).
const BUILDER_PLAN_GATED_ROUTE_PREFIXES = [
  '/journeys/analysis',
  '/journeys/reports',
  '/journeys/campaigns',
  '/journeys/automations',
  '/journeys/message-library',
  '/journeys/goals',
  '/journeys/objects',
  '/journeys/integrations',
  '/journeys/layouts',
  '/journeys/collections',
  '/journeys/settings/objects',
];

const ROUTE_GUARDS = [
  {
    test: (r) => r.includes('/analysis/deliverability') || r.includes('/analysis/email-clients'),
    guard: ({ account, hasFeatureFlag }) =>
      account?.plan_slug !== BUILDER_PLAN_SLUG && hasFeatureFlag(AccountFeatureFlag.DeliverabilityAnalytics),
  },
  {
    test: (r) => BUILDER_PLAN_GATED_ROUTE_PREFIXES.some((prefix) => r.includes(prefix)),
    guard: ({ account }) => account?.plan_slug !== BUILDER_PLAN_SLUG,
  },
  {
    test: (r) => r.includes('/journeys/anonymous'),
    guard: ({ account }) => account?.planFeatures[PlanFeature.AnonymousInApp].enabled ?? false,
  },
  {
    test: (r) => /\/workspaces\/(last|\d+)\/(settings\/geofences|(journeys\/)?geofences)(\/|$)/.test(r),
    // Match the geofences routes' own guard: they redirect users lacking
    // journeys/geofences/view, so don't offer them as search results.
    guard: ({ account, hasPermission }) =>
      account != null &&
      account.planFeatures != null &&
      hasUnlimitedPlanFeatureAccess(account, PlanFeature.Geofences) &&
      hasPermission('journeys/geofences/view'),
  },
  {
    test: (r) => r.includes('/journeys/integrations'),
    guard: ({ hasAnyPermission }) => hasAnyPermission('journeys/imports/view', 'journeys/exports/view'),
  },
  {
    test: (r) => r.includes('/journeys/imports'),
    guard: ({ hasPermission }) => hasPermission('journeys/imports/view'),
  },
  {
    test: (r) => r.includes('/journeys/exports'),
    guard: ({ hasPermission }) => hasPermission('journeys/exports/view'),
  },
  {
    test: (r) => r.includes('/design-studio/'),
    guard: ({ hasPermission }) => hasPermission('parcel/emails/view') && hasPermission('parcel/components/view'),
  },
  {
    test: (r) => r.includes('/agent'),
    guard: ({ account }) => account?.public_settings?.ai_enabled === true,
  },
  {
    test: (r) => /\/workspaces\/(last|\d+)\/settings(\/|$)/.test(r),
    guard: ({ hasPermission }) => hasPermission('journeys/workspace_settings/view'),
  },
  {
    test: (r) => /\/workspaces\/(last|\d+)\/journeys\/settings(\/|$)/.test(r),
    guard: ({ hasPermission }) => hasPermission('journeys/workspace_settings/view'),
  },
  {
    test: (r) =>
      r.includes('/journeys/settings/api_credentials') ||
      r.includes('/settings/api_credentials') ||
      r.includes('/settings/api-keys'),
    guard: ({ hasPermission }) => hasPermission('account/api_credentials/view'),
  },
  {
    test: (r) =>
      r.includes('/journeys/settings/webhook_configuration') || r.includes('/settings/webhook_configuration'),
    guard: ({ hasPermission }) => hasPermission('journeys/configuration_manager/view'),
  },
  {
    test: (r) => r.includes('/settings/ai-business-profile'),
    guard: ({ account }) => account?.public_settings?.ai_enabled === true,
  },
  {
    test: (r) => r.includes('/settings/actions/email/account_verification'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.AccountVerification),
  },
  {
    test: (r) => r === '/settings/ai',
    guard: ({ hasPermission }) => hasPermission('account/support_access/view'),
  },
  {
    test: (r) => r.includes('/settings/llm_configuration'),
    guard: ({ hasFeatureFlag, hasPermission }) =>
      hasFeatureFlag(AccountFeatureFlag.LLMActionBYOK) && hasPermission('journeys/configuration_manager/view'),
  },
  {
    test: (r) =>
      r.startsWith('/settings/billing') || r.startsWith('/settings/subscription') || r.startsWith('/settings/receipts'),
    guard: ({ hasPermission }) => hasPermission('account/billing/view'),
  },
  {
    test: (r) => r.startsWith('/settings/audit-logs'),
    guard: ({ hasPermission }) => hasPermission('account/view'),
  },
  {
    test: (r) => r.startsWith('/settings/security') || r.startsWith('/settings/account'),
    guard: ({ hasPermission }) => hasPermission('account/view'),
  },
  {
    test: (r) => r.startsWith('/settings/roles'),
    guard: ({ hasPermission }) => hasPermission('account/team_members/view'),
  },
  {
    test: (r) => r.startsWith('/settings/api'),
    guard: ({ hasPermission }) => hasPermission('account/api_credentials/view'),
  },
  {
    test: (r) => r.includes('/settings/actions/sms-providers'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.CustomSms),
  },
] satisfies { test: (url: string) => boolean; guard: GuardFunction }[];

function isAllowed(url: string, guard: PermissionGuard): boolean {
  for (const { test, guard: guardFunction } of ROUTE_GUARDS) {
    if (test(url) && !guardFunction(guard)) return false;
  }
  return true;
}

// The manifest is the source of truth for navigable routes. getNavigationLabels resolves
// taxonomy-gated routes (renamed per-account) for the active variant. Deduplicate by label
// so aliased paths (e.g. two routes sharing a "Workspace settings" label) don't produce
// duplicate results.
function getNavigableRoutes(taxonomy: ProductTaxonomy): ReadonlyArray<{ label: string; routeTemplate: string }> {
  return [
    ...Object.entries(getNavigationLabels(taxonomy))
      .reduce((map, [routeTemplate, label]) => {
        if (!map.has(label)) map.set(label, { label, routeTemplate });
        return map;
      }, new Map<string, { label: string; routeTemplate: string }>())
      .values(),
  ];
}

const MAX_RESULTS = 10;

export function searchNavigation({
  query,
  workspaceId,
  guard,
  taxonomy = createProductTaxonomy('current'),
}: {
  query: string;
  workspaceId: number;
  guard: PermissionGuard | null;
  taxonomy?: ProductTaxonomy;
}): Extract<SearchResult, { kind: 'navigation' }>[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2 || workspaceId === 0) return [];

  const matches: Extract<SearchResult, { kind: 'navigation' }>[] = [];
  for (const { label, routeTemplate } of getNavigableRoutes(taxonomy)) {
    if (defaultFilter(label, q) === 0) continue;
    const url = routeTemplate
      // Child-app routes use the manual ROUTES param convention.
      .replace(':workspaceId', String(workspaceId))
      // Hydra-owned routes use TanStack Router's generated param convention.
      .replace('$workspaceId', String(workspaceId))
      // TanStack index routes include a trailing slash; query URLs should not become `/path/?key=value`.
      .replace('/?', '?');
    if (guard != null && !isAllowed(url, guard)) continue;
    matches.push({ kind: 'navigation', title: label, url, doc_id: url });
  }

  // Prefer labels that start with the query over fuzzy mid-string matches
  matches.sort((a, b) => {
    const aStarts = a.title.toLowerCase().startsWith(q);
    const bStarts = b.title.toLowerCase().startsWith(q);
    if (aStarts && !bStarts) return -1;
    if (bStarts && !aStarts) return 1;
    return 0;
  });

  return matches.slice(0, MAX_RESULTS);
}

export function navigationSearchOptions(
  query: string,
  workspaceId: number,
  guard: PermissionGuard | null,
  { taxonomy }: { taxonomy?: ProductTaxonomy } = {},
) {
  return queryOptions({
    // guard and taxonomy are intentionally excluded from the key — permission and per-account
    // flag changes mid-session are rare and typically accompanied by a full page reload that
    // clears the cache anyway.
    queryKey: ['navigationSearch', query, workspaceId] as const,
    queryFn: async () => {
      // Artificial delay so instant local results don't feel broken to users expecting processing time
      await new Promise<void>((resolve) => setTimeout(resolve, 100));
      return searchNavigation({ query, workspaceId, guard, taxonomy });
    },
    staleTime: Infinity,
  });
}
